import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useSubscription, TIERS } from '@/features/subscription/hooks/useSubscription';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { ArrowLeft, PartyPopper, Check } from 'lucide-react';
import { PlanCards } from '@/features/subscription/components/PlanCards';
import { ROIBanner } from '@/features/subscription/components/ROIBanner';
import { PricingFAQ } from '@/features/subscription/components/PricingFAQ';

const Pricing = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tier, subscriptionEnd, trialEnd, subscriptionStatus, billingInterval, loading, startCheckout, checkSubscription, isLegacyFree, manageSubscription, isInTrial } = useSubscription();
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setShowSuccess(true);
      checkSubscription();
      setTimeout(() => checkSubscription(), 3000);
      setSearchParams({}, { replace: true });
    }
    if (searchParams.get('canceled') === 'true') {
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!loading && tier === 'free' && !isInTrial) {
    return <Navigate to="/trial-expired" replace />;
  }

  return (
    <div className="pb-20 space-y-10 max-w-5xl mx-auto px-4 pt-6">

      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/90 backdrop-blur-md p-6"
          >
            <div className="max-w-sm w-full text-center space-y-6">
              <motion.div
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
                className="w-20 h-20 rounded-3xl bg-primary/15 flex items-center justify-center mx-auto"
              >
                <PartyPopper size={36} className="text-primary" />
              </motion.div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold font-display">Bienvenue dans votre nouveau plan !</h2>
                <p className="text-sm text-muted-foreground">Votre abonnement est actif. Toutes vos fonctionnalités sont disponibles immédiatement.</p>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                <Check size={14} className="text-primary" />
                <span className="text-sm font-semibold text-primary">Plan actif — {TIERS[tier].name['fr']}</span>
              </div>
              <button
                onClick={() => { setShowSuccess(false); navigate('/'); }}
                className="w-full py-3.5 bg-primary text-primary-foreground rounded-2xl font-semibold text-base hover:bg-primary/90 transition-colors"
              >
                Commencer à utiliser Consentink
              </button>
              <button onClick={() => setShowSuccess(false)} className="text-sm text-muted-foreground hover:text-foreground">
                Voir les plans
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/')} className="p-2 rounded-xl hover:bg-secondary transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold font-display">Tarifs simples et transparents</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Dédié aux professionnels du tatouage, piercing, microblading et esthétique
          </p>
        </div>
      </div>

      <ROIBanner />

      <PlanCards
        showToggle
        tier={tier}
        isLegacyFree={isLegacyFree}
        startCheckout={startCheckout}
        manageSubscription={manageSubscription}
        subscriptionEnd={subscriptionEnd}
        subscriptionStatus={subscriptionStatus}
        billingInterval={billingInterval}
        trialEnd={trialEnd}
      />

      {subscriptionEnd && tier !== 'free' && (
        <p className="text-center text-xs text-muted-foreground">
          Expire le {new Date(subscriptionEnd).toLocaleDateString('fr-FR')}
        </p>
      )}

      <PricingFAQ />

      <button
        onClick={() => { checkSubscription(); }}
        className="mx-auto block px-6 py-2.5 bg-secondary rounded-xl text-sm text-muted-foreground hover:bg-secondary/80 transition-colors"
      >
        Actualiser le statut
      </button>
    </div>
  );
};

export default Pricing;
